// components/BuildingSelector.jsx
/**
 * Building selector component that allows users to choose a building from a list.
 * Provides a dropdown interface for building selection with internationalization support.
 */

import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

/**
 * BuildingSelector component that renders a dropdown menu for building selection.
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} props.buildingData - Array of building objects containing building information
 * @param {Object} props.selectedBuilding - Currently selected building object
 * @param {Function} props.onBuildingSelect - Callback function called when a building is selected
 * @param {string} props.className - Optional className for the component
 * @returns {JSX.Element} The rendered BuildingSelector component
 */
const BuildingSelector = ({
  buildingData,
  selectedBuilding,
  onBuildingSelect,
  className,
}) => {
  const { t } = useTranslation();

  return (
    <div className={className}>
      <div className="relative w-full">
        <select
          value={selectedBuilding?.Name || ""}
          onChange={(e) => {
            const selected = buildingData.find(
              (b) => b.Name === e.target.value
            );
            onBuildingSelect(selected);
          }}
          className="w-full h-7 text-xs px-2 py-0 border rounded focus:outline-none focus:ring-1 focus:ring-[#cabb78] appearance-none bg-white pr-8"
        >
          <option value="" disabled>
            {t("select_a_building")}
          </option>
          {buildingData.map((building) => (
            <option
              key={building.Name}
              value={building.Name}
              className="text-xs py-0"
            >
              {building.Name}
            </option>
          ))}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
          <svg
            className="h-4 w-4 fill-current"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
          >
            <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
          </svg>
        </div>
      </div>
    </div>
  );
};

// PropTypes validation for BuildingSelector component
BuildingSelector.propTypes = {
  buildingData: PropTypes.arrayOf(
    PropTypes.shape({
      BGuid: PropTypes.string.isRequired,
      Name: PropTypes.string.isRequired,
      Address: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedBuilding: PropTypes.shape({
    BGuid: PropTypes.string,
    Name: PropTypes.string,
    // Add other building properties you're using
  }),
  onBuildingSelect: PropTypes.func.isRequired,
  className: PropTypes.string,
};

export default BuildingSelector;
